import { useEffect } from 'react';
import { useLocation } from 'react-router';

const SITE_NAME = 'Boletra';
const DEFAULT_OG = '/og-default.jpg';
// Default OG image dimensions (the file in /public is exactly this size).
// When a caller passes a custom ogImage we omit width/height so social
// crawlers fall back to their own discovery.
const DEFAULT_OG_WIDTH = 1200;
const DEFAULT_OG_HEIGHT = 630;
const SUPPORTED_LOCALES = ['es', 'en'] as const;
type Locale = (typeof SUPPORTED_LOCALES)[number];

export interface SeoProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  // Optional explicit dimensions for the OG image. Pass when known so
  // crawlers can layout the card without fetching the file first.
  ogImageWidth?: number;
  ogImageHeight?: number;
  // 'website' for landing/listing, 'article' for events.
  ogType?: 'website' | 'article' | 'event';
  // Extra <script type="application/ld+json"> blocks (Event, Organization…)
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  // If true the page should not be indexed (e.g. dashboard).
  noindex?: boolean;
  // Locale of the current page; used for og:locale and html[lang].
  locale?: Locale;
}

/**
 * Lightweight head manager. We don't ship react-helmet to keep the SPA bundle
 * small; this component sets/updates the same set of tags on every render.
 *
 * Server-side rendering is not enabled — Googlebot does run JS so this works
 * for indexing, but for the very first paint we keep a safe baseline in
 * `index.html`. Anything beyond that gets overwritten here.
 */
export function Seo({
  title,
  description,
  canonical,
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogType = 'website',
  jsonLd,
  noindex,
  locale,
}: SeoProps) {
  const location = useLocation();

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`;
    document.title = fullTitle;

    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    const path = canonical ?? `${location.pathname}${location.search}`;
    const canonicalUrl = path.startsWith('http') ? path : `${origin}${path}`;
    const effectiveImage = ogImage ?? DEFAULT_OG;
    const effectiveImageUrl = effectiveImage.startsWith('http')
      ? effectiveImage
      : `${origin}${effectiveImage}`;

    setMeta('description', description);
    setMeta('robots', noindex ? 'noindex,nofollow' : 'index,follow');

    setProperty('og:site_name', SITE_NAME);
    setProperty('og:title', fullTitle);
    setProperty('og:description', description);
    setProperty('og:type', ogType);
    setProperty('og:url', canonicalUrl);
    setProperty('og:image', effectiveImageUrl);
    if (!ogImage) {
      setProperty('og:image:width', String(DEFAULT_OG_WIDTH));
      setProperty('og:image:height', String(DEFAULT_OG_HEIGHT));
      setProperty('og:image:alt', `${SITE_NAME} · ${description.slice(0, 100)}`);
    } else {
      removeProperty('og:image:width');
      removeProperty('og:image:height');
      if (ogImageWidth) setProperty('og:image:width', String(ogImageWidth));
      if (ogImageHeight) setProperty('og:image:height', String(ogImageHeight));
      setProperty('og:image:alt', title);
    }
    if (locale) setProperty('og:locale', locale === 'es' ? 'es_LA' : 'en_US');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', effectiveImageUrl);

    setLink('canonical', canonicalUrl);

    if (locale) {
      document.documentElement.lang = locale;
    }

    setHreflangAlternates(canonicalUrl, locale);

    setJsonLd(jsonLd);

    return () => {
      // We deliberately do not remove the meta tags on unmount — the next
      // page's <Seo> overwrites them. Only clear our managed JSON-LD slot.
      removeJsonLd();
    };
  }, [
    title,
    description,
    canonical,
    ogImage,
    ogImageWidth,
    ogImageHeight,
    ogType,
    noindex,
    jsonLd,
    locale,
    location.pathname,
    location.search,
  ]);

  return null;
}

/**
 * Build a `BreadcrumbList` schema.org payload from an ordered list of
 * (name, url) pairs. The first item is usually "Inicio"/"Home" and the last
 * one is the current page. Compose it into `Seo`'s `jsonLd` array.
 */
export function buildBreadcrumbList(
  items: Array<{ name: string; url: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

function setMeta(name: string, content: string) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setProperty(property: string, content: string) {
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeProperty(property: string) {
  document.head.querySelectorAll(`meta[property="${property}"]`).forEach((el) => el.remove());
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]:not([hreflang])`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setHreflangAlternates(canonicalUrl: string, currentLocale?: Locale) {
  // Wipe any prior hreflang links we set, then recreate based on URL pattern.
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang][data-boletra]')
    .forEach((el) => el.remove());
  if (!canonicalUrl.startsWith('http')) return;
  let pathStart: number;
  try {
    const parsed = new URL(canonicalUrl);
    pathStart = parsed.pathname.startsWith('/') ? 1 : 0;
    const segments = parsed.pathname.slice(pathStart).split('/');
    const head = segments[0];
    if (!head || !(SUPPORTED_LOCALES as readonly string[]).includes(head)) return;
    const tail = segments.slice(1).join('/');
    for (const l of SUPPORTED_LOCALES) {
      const altPath = `/${l}${tail ? `/${tail}` : ''}`;
      const href = `${parsed.origin}${altPath}${parsed.search}`;
      appendHreflang(l, href);
    }
    appendHreflang('x-default', `${parsed.origin}/es${tail ? `/${tail}` : ''}${parsed.search}`);
  } catch {
    return;
  }
  void currentLocale;
}

function appendHreflang(hreflang: string, href: string) {
  const el = document.createElement('link');
  el.setAttribute('rel', 'alternate');
  el.setAttribute('hreflang', hreflang);
  el.setAttribute('href', href);
  el.setAttribute('data-boletra', '1');
  document.head.appendChild(el);
}

const JSON_LD_ID = 'boletra-jsonld';
function setJsonLd(data: SeoProps['jsonLd']) {
  removeJsonLd();
  if (!data) return;
  const arr = Array.isArray(data) ? data : [data];
  for (const obj of arr) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.boletra = JSON_LD_ID;
    script.textContent = JSON.stringify(obj);
    document.head.appendChild(script);
  }
}

function removeJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][data-boletra="${JSON_LD_ID}"]`)
    .forEach((el) => el.remove());
}
